import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import DashboardShell from '../../components/DashboardShell/DashboardShell';
import PageHeader from '../../components/PageHeader/PageHeader';
import WalletBadge from '../../components/WalletBadge/WalletBadge';
import styles from './Rank.module.css';
import axiosClient from '../../api/axiosClient';
import { useCurrency } from '../../app/CurrencyContext';

interface RankRow {
  level: number; name: string; selfUsd: number; teamUsd: number; rewardUsd: number | null;
  awardId: string | null; status: 'claimable' | 'claimed' | 'locked' | 'skipped';
  rewardInr: number | null; awardedAt: string | null; claimedAt: string | null;
}
interface Share { id: string; period: string; shareUsd: number; shareInr: number; status: 'claimable' | 'claimed' }
interface RankData {
  usdRate: number; selfUsd: number; teamUsd: number; currentRank: string | null; currentLevel: number; onHold: boolean;
  next: null | { level: number; name: string; selfUsd: number; teamUsd: number; selfRemainingUsd: number; teamRemainingUsd: number };
  ranks: RankRow[];
  brandClub: { member: boolean; shares: Share[] };
}

const usd = (n: number) => `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
const pct = (have: number, need: number) => Math.max(0, Math.min(100, need > 0 ? (have / need) * 100 : 100));

const needMore = (have: number, need: number) => Math.max(0, Math.round((need - have) * 100) / 100);

// Which of the two targets of a rank are already met.
const progressOf = (selfUsd: number, teamUsd: number, rank: { selfUsd: number; teamUsd: number }) => ({
  selfDone: selfUsd >= rank.selfUsd,
  teamDone: teamUsd >= rank.teamUsd,
});

const Rank: React.FC = () => {
  const { formatFromInr } = useCurrency();
  const [data, setData] = useState<RankData | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = () => {
    axiosClient.get('/rank/mine').then(({ data }) => setData(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const claim = async (url: string, id: string) => {
    setBusy(id);
    try {
      const { data: res } = await axiosClient.post(url);
      toast.success(Number(res.chargeInr) > 0
        ? `Claimed ${formatFromInr(Number(res.claimedInr))}. Admin charge ${formatFromInr(Number(res.adminInr))} + TDS ${formatFromInr(Number(res.tdsInr))} deducted, ${formatFromInr(Number(res.netInr))} is now in your wallet.`
        : `Claimed ${formatFromInr(Number(res.claimedInr))}. It is now in your wallet.`);
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not claim right now.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <DashboardShell>
      <PageHeader title="My Rank" subtitle="Advisory Board ranks. Each rank pays a one-time reward." action={<WalletBadge />} />
      <div className={styles.page}>
        {loading && <div className={styles.muted}>Loading…</div>}
        {data && (
          <>
            {data.onHold && (
              <div className={styles.hold}>
                You have no active investment, so your unclaimed rewards are on hold. They become claimable when you invest again.
              </div>
            )}

            <div className={styles.hero}>
              <div><div className={styles.heroLabel}>Current rank</div><div className={styles.heroValue}>{data.currentRank || 'No rank yet'}</div></div>
              <div><div className={styles.heroLabel}>My investment</div><div className={styles.heroValue}>{usd(data.selfUsd)}</div></div>
              <div><div className={styles.heroLabel}>Team business</div><div className={styles.heroValue}>{usd(data.teamUsd)}</div></div>
            </div>

            {data.next && (() => {
              const nx = data.next;
              const { selfDone, teamDone } = progressOf(data.selfUsd, data.teamUsd, nx);
              return (
                <div className={styles.next}>
                  <b>Next rank: {nx.name}</b>
                  <div style={{ marginTop: 12 }}>
                    <div className={styles.barRow}>
                      <span>My investment {selfDone && <em className={styles.done}>✓ Done</em>}</span>
                      <span>{usd(data.selfUsd)} / {usd(nx.selfUsd)}</span>
                    </div>
                    <div className={`${styles.bar} ${selfDone ? styles.barDone : ''}`}><div style={{ width: `${pct(data.selfUsd, nx.selfUsd)}%` }} /></div>
                    {!selfDone && <div className={styles.needLine}>{usd(needMore(data.selfUsd, nx.selfUsd))} more needed</div>}

                    <div className={styles.barRow}>
                      <span>Team business {teamDone && <em className={styles.done}>✓ Done</em>}</span>
                      <span>{usd(data.teamUsd)} / {usd(nx.teamUsd)}</span>
                    </div>
                    <div className={`${styles.bar} ${teamDone ? styles.barDone : ''}`}><div style={{ width: `${pct(data.teamUsd, nx.teamUsd)}%` }} /></div>
                    {!teamDone && <div className={styles.needLine}>{usd(needMore(data.teamUsd, nx.teamUsd))} more needed</div>}
                  </div>
                  <div className={styles.muted}>
                    {selfDone && !teamDone && 'Self target done. Team business is still needed. Both are required to unlock this rank.'}
                    {!selfDone && teamDone && 'Team target done. Your own investment is still needed. Both are required to unlock this rank.'}
                    {!selfDone && !teamDone && 'Both targets are required to unlock this rank. Your team is the 7 levels under you.'}
                  </div>
                </div>
              );
            })()}

            <div className={styles.sectionTitle}>All ranks</div>
            <div className={styles.grid}>
              {data.ranks.map((r) => (
                <div key={r.level} className={`${styles.rank} ${r.level === data.currentLevel ? styles.current : ''}`}>
                  <div className={styles.rankName}>{r.level}. {r.name}</div>
                  <div className={styles.req}>Self {usd(r.selfUsd)} · Team {usd(r.teamUsd)}</div>
                  <div className={styles.reward}>{r.rewardUsd != null ? usd(r.rewardUsd) : ''}</div>
                  {(() => {
                    const { selfDone, teamDone } = progressOf(data.selfUsd, data.teamUsd, r);
                    const lockedText = r.status !== 'locked' ? ''
                      : selfDone && !teamDone ? 'Self done · Team pending'
                      : !selfDone && teamDone ? 'Team done · Self pending'
                      : 'Locked';
                    return (
                      <span className={`${styles.pill} ${styles[r.status]}`}>
                        {r.status === 'claimable' ? 'Ready' : r.status === 'claimed' ? 'Claimed' : r.status === 'skipped' ? 'Skipped' : lockedText}
                      </span>
                    );
                  })()}
                  {r.status === 'claimable' && r.awardId && (
                    <button className={styles.btn} disabled={busy === r.awardId} onClick={() => claim(`/rank/awards/${r.awardId}/claim`, r.awardId!)}>
                      {busy === r.awardId ? 'Claiming…' : `Claim ${r.rewardInr != null ? formatFromInr(r.rewardInr) : ''}`}
                    </button>
                  )}
                </div>
              ))}
            </div>

            {(data.brandClub.member || data.brandClub.shares.length > 0) && (
              <>
                <div className={styles.sectionTitle}>Brand Club</div>
                <div className={styles.muted} style={{ marginBottom: 10 }}>
                  Global Promoters share 5% of the company net profit, split equally for each period.
                </div>
                <div className={styles.grid}>
                  {data.brandClub.shares.length === 0 && <div className={styles.muted}>No profit share has been announced yet.</div>}
                  {data.brandClub.shares.map((s) => (
                    <div key={s.id} className={styles.rank}>
                      <div className={styles.rankName}>Period {s.period}</div>
                      <div className={styles.req}>Your share</div>
                      <div className={styles.reward}>{usd(s.shareUsd)}</div>
                      <span className={`${styles.pill} ${s.status === 'claimed' ? styles.claimed : styles.claimable}`}>{s.status === 'claimed' ? 'Claimed' : 'Ready'}</span>
                      {s.status === 'claimable' && (
                        <button className={styles.btn} disabled={busy === s.id} onClick={() => claim(`/rank/brand-club/${s.id}/claim`, s.id)}>
                          {busy === s.id ? 'Claiming…' : `Claim ${formatFromInr(s.shareInr)}`}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </DashboardShell>
  );
};

export default Rank;